import './tree.css';
import { ActionIcon, Button, Center, Group, Loader, Menu, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { hideNotification, showNotification } from '@mantine/notifications';
import {
  IconCheck,
  IconCirclePlus,
  IconDotsVertical,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import cx from 'clsx';
import Tree from 'rc-tree';
import { DataNode, Key } from 'rc-tree/lib/interface';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  addFAQsService,
  deleteFAQsService,
  FAQsBase,
  faqsChildNoe,
  FAQsItem,
  FAQTreeItem,
  I18N_LANG_MAP,
  updateFAQsService,
  useFAQTreeDragSort,
  exportFAQTree,
} from '@mygpt/app';

import { FaqsItemEdit } from '@/components/Faqs/item-edit';
import { useDatasetStore } from '@/store/dataset';
import classes from './index.module.css';
import { ImportModal } from '@/components/Faqs/Tree/ImportModal';

export interface MyTreeNode extends DataNode {
  key: string;
  title: string;
  children: MyTreeNode[];
  customData?: FAQsItem;
}

const FAQAttributeAutoComplement = {
  id: '0',
  question: '',
  answer: '',
  created_at: '',
  updated_at: '',
  answer_characters_count: 0,
  has_image: false,
  user_id: '',
  answer_token_count: 0,
  sources: '',
  rating: 0,
  similar_questions: [],
  recommended_questions: [],
};

export const FaqsTree = ({ datasetId: id }: { datasetId?: string }) => {
  const { i18n, t } = useTranslation();

  const { datasetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // hierarchyLevel 层级，暂时没用到
  const { hierarchyParentId, nodeId } = location.state || {};
  const { isCloning, setCloningTipOpened } = useDatasetStore();
  const [treeData, setTreeData] = useState<MyTreeNode[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [activeFaq, setActiveFaq] = useState<FAQsItem>();
  const [faqLoading, setFaqLoading] = useState<boolean>(true);
  const [treeLoading, setTreeLoading] = useState<boolean>(true);
  const [addConfirmOpened, { close: addConfirmClose, open: addConfirmOpen }] = useDisclosure(false);
  const addNodeParentKeyRef = useRef<string>('');
  const [deletingNodeKey, setDeletingNodeKey] = useState<string[]>([]);
  const parentNodeKeyRef = useRef<string>('');
  const [importModalOpened, { open: openImportModal, close: closeImportModal }] = useDisclosure(false);

  const [deleteConfirmOpened, { close: deleteConfirmClose, open: deleteConfirmOpen }] = useDisclosure(false);
  const deleteNodeKeyRef = useRef<string>('');
  const deleteNodeTitleRef = useRef<string>('');

  // 获取所有节点的 key（用于展开/折叠）
  const getAllExpandableNodeKeys = (data: MyTreeNode[]): Key[] => {
    const keys: Key[] = [];
    const loop = (nodes: MyTreeNode[]) => {
      nodes.forEach((node) => {
        if (node.key !== '0') {
          // 排除新增节点
          keys.push(node.key);
        }
        if (node.children?.length) {
          loop(node.children);
        }
      });
    };
    loop(data);
    return keys;
  };

  // 获取所有可展开节点的 key
  const allExpandableKeys = getAllExpandableNodeKeys(treeData);

  // 判断是否所有节点都已展开
  const isAllExpanded = allExpandableKeys.length > 0 && expandedKeys.length === allExpandableKeys.length;

  // 切换展开/折叠状态
  const handleToggleExpand = () => {
    if (isAllExpanded) {
      setExpandedKeys([]);
    } else {
      setExpandedKeys(allExpandableKeys);
    }
  };

  // 使用拖拽排序 Hook
  const { activeId, sortLoading, sensors, getAllNodeKeys, findNodeById, handleDragStart, handleDragEnd } =
    useFAQTreeDragSort({
      treeData,
      setTreeData,
      datasetId: datasetId || id || '',
    });

  const init = async () => {
    const validDatasetId = datasetId || id;
    if (!validDatasetId) {
      console.warn('datasetId is null');
      return;
    }

    const params = {
      datasetId: validDatasetId,
      hierarchy_parent_id: '0',
      num_levels: 0,
      language: I18N_LANG_MAP[i18n.language],
    };
    const { data: resData } = await faqsChildNoe(params);

    if ('status' in resData && resData.status === 'importing') {
      const { import_progress } = resData;
      const { success_faqs, failed_faqs, total_faqs } = import_progress;

      showNotification({
        id: 'importing-faq-list',
        message: t('create.faqs.translating', {
          success_count: `${success_faqs}/${total_faqs}`,
          failed_count: `${failed_faqs}/${total_faqs}`,
        }),
        color: 'white',
        icon: <Loader size={20} />,
        autoClose: false,
        withCloseButton: false,
      });
      setFaqLoading(false);
      // 清空列表数据
      setTreeData([]);

      setTimeout(() => {
        init();
      }, 5000);
      return;
    }

    hideNotification('importing-faq-list');

    const loop = (data: FAQTreeItem[]): MyTreeNode[] => {
      const arr: MyTreeNode[] = [];
      data.forEach((item) => {
        const newItem: MyTreeNode = {
          key: item.id,
          title: item.question,
          customData: item,
          children: [],
        };

        if (item.child_nodes.length) {
          newItem.children = loop(item.child_nodes);
        }
        arr.push(newItem);
      });
      return arr;
    };
    const resTreeData: MyTreeNode[] = loop(resData as FAQTreeItem[]);

    if (resTreeData && resTreeData.length) {
      setTreeData(resTreeData);
    } else if (!treeData.length) {
      add();
    }

    setTreeLoading(false);
    if (!hierarchyParentId) {
      setSelectedKeys([resTreeData.length ? resTreeData[0].key : '0']);
    } else {
      const defaultExpandedKeys: Key[] = [];

      const searchTree = (data: MyTreeNode[], path: Key[] = []) => {
        data.forEach((node) => {
          const currentPath = path.concat([node.key]);

          if (node.key === hierarchyParentId) {
            defaultExpandedKeys.push(...currentPath);
          } else if (node.children.length) {
            searchTree(node.children, currentPath);
          }
        });
      };

      searchTree(resTreeData);

      setExpandedKeys(defaultExpandedKeys);
      setSelectedKeys([nodeId]);

      // 在处理完state后清除state
      navigate(location.pathname, { replace: true });
    }
  };

  const detail = (node: MyTreeNode) => {
    setFaqLoading(true);
    Promise.resolve()
      .then(() => {
        const faqData = node.customData;
        if (faqData) {
          faqData.is_search = true;
        }
        setActiveFaq(faqData);
      })
      .finally(() => {
        setTimeout(() => {
          setFaqLoading(false);
        }, 250);
      });
  };

  const checkHasAddNode = (): boolean => {
    let hasAddNode = false;

    const loop = (data: MyTreeNode[]) => {
      data.map((item) => {
        if (item.key === '0') {
          hasAddNode = true;
        } else if (item.children?.length) {
          loop(item.children);
        }
      });
    };
    loop(treeData);
    return hasAddNode;
  };

  const cleanAddNode = (treeData: MyTreeNode[]) => {
    const loop = (data: MyTreeNode[]) => {
      data.map((item, index) => {
        if (item.key === '0') {
          data.splice(index, 1);
        } else if (item.children?.length) {
          loop(item.children);
        }
      });
    };
    loop(treeData);
  };

  const hasChildNodes = (nodeKey: string): boolean => {
    let hasChildren = false;

    const loop = (data: MyTreeNode[]) => {
      data.forEach((item) => {
        if (item.key === nodeKey && item.children && item.children.length > 0) {
          hasChildren = true;
        } else if (item.children?.length) {
          loop(item.children);
        }
      });
    };

    loop(treeData);
    return hasChildren;
  };

  const findNodeTitle = (nodeKey: string): string => {
    let title = '';

    const loop = (data: MyTreeNode[]) => {
      data.forEach((item) => {
        if (item.key === nodeKey) {
          title = item.title;
        } else if (item.children?.length) {
          loop(item.children);
        }
      });
    };

    loop(treeData);
    return title;
  };

  const add = (parentNodeKey?: string, force?: boolean) => {
    const hasAdd = checkHasAddNode();
    if (!force && hasAdd) {
      addNodeParentKeyRef.current = parentNodeKey || '';
      addConfirmOpen();
      return;
    }

    const newTreeData = [...treeData];
    cleanAddNode(newTreeData);
    const defaultKey = '0';
    const defaultData = [
      {
        customData: { ...FAQAttributeAutoComplement, hierarchy_parent_id: parentNodeKey || '0' },
        key: defaultKey,
        title: t('faqs.tree.default_title'),
        isLeaf: true,
        children: [],
      },
    ];

    if (parentNodeKey) {
      parentNodeKeyRef.current = parentNodeKey;
      const loop = (data: MyTreeNode[]) => {
        data.map((item) => {
          if (item.key === parentNodeKey) {
            item.isLeaf = false;
            item.children.push(...defaultData);
            setExpandedKeys([...expandedKeys, item.key]);
          } else if (item.children?.length) {
            loop(item.children);
          }
        });
      };
      loop(newTreeData);
    } else {
      newTreeData.push(...defaultData);
    }
    setTreeData(newTreeData);
    setSelectedKeys([defaultKey]);
    addConfirmClose();
  };

  const del = (nodeKey: string) => {
    // 检查是否有子节点
    if (nodeKey !== '0' && hasChildNodes(nodeKey)) {
      const nodeTitle = findNodeTitle(nodeKey);
      deleteNodeKeyRef.current = nodeKey;
      deleteNodeTitleRef.current = nodeTitle;
      deleteConfirmOpen();
      return;
    }

    // 执行删除操作
    executeDelete(nodeKey);
  };

  const executeDelete = (nodeKey: string) => {
    const handleDeleteNode = () => {
      const updateTreeData = (data: MyTreeNode[]): MyTreeNode[] => {
        return data
          .map((item): MyTreeNode | null => {
            if (item.key === nodeKey) {
              return null; // 如果是目标节点，则删除它
            } else if (item.children?.length) {
              const updatedChildren = updateTreeData(item.children);
              return {
                ...item,
                children: updatedChildren,
                isLeaf: updatedChildren.length === 0, // 更新 isLeaf 属性
              };
            } else {
              return item; // 如果没有子节点或不是目标节点，则直接返回
            }
          })
          .filter((item): item is MyTreeNode => item !== null); // 移除返回的 null 值
      };

      const newTreeData = updateTreeData(treeData);
      setTreeData(newTreeData);
      setSelectedKeys([]);
    };

    if (nodeKey === '0') {
      handleDeleteNode();
      return;
    }

    setDeletingNodeKey([...deletingNodeKey, nodeKey]);
    deleteFAQsService({ datasetId, faqsId: nodeKey })
      .then(() => {
        handleDeleteNode();
      })
      .finally(() => {
        setDeletingNodeKey(deletingNodeKey.filter((item) => item !== nodeKey));
      });
  };

  const onExpand = (expandedKeysValue: Key[]) => {
    // 如果需要的话，可以在这里做额外的处理
    setExpandedKeys(expandedKeysValue);
  };

  const onSelect = (selectedKeysValue: Key[]) => {
    setSelectedKeys(selectedKeysValue);
  };

  const onDetailNodeSubmit = (faq: FAQsBase) => {
    const newTreeData = [...treeData];
    const key = selectedKeys[0];

    return new Promise<void>((resolve) => {
      const isModify = activeFaq?.id !== '0';

      const methodFAQsService = isModify ? updateFAQsService : addFAQsService;
      const param = {
        ...faq,
        datasetId: datasetId || id || '',
        hierarchy_parent_id: activeFaq?.hierarchy_parent_id,
        faqsId: activeFaq?.id || '',
        is_search: !!activeFaq?.is_search,
      };

      methodFAQsService(param)
        .then((res) => {
          const nodeKey = res.data.id;
          const loop = (data: MyTreeNode[]) => {
            data.map((item) => {
              if (item.key === key) {
                item.title = faq.question;
                item.customData = res.data;
                item.key = nodeKey;
              } else if (item.children?.length) {
                loop(item.children);
              }
            });
          };
          loop(newTreeData);
          setTreeData(newTreeData);

          // 新增后需要选中新的key
          if (!selectedKeys.includes(nodeKey)) {
            setSelectedKeys([nodeKey]);
          }

          const checkIcon = <IconCheck />;
          showNotification({
            message: t('save_success'),
            color: 'green.5',
            icon: checkIcon,
          });
        })
        .finally(() => {
          resolve();
        });
    });
  };

  const isDeletingNode = (key: string) => deletingNodeKey.includes(key);

  const onCloseImportModal = () => {
    closeImportModal();
    init();
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    const validDatasetId = datasetId || id;
    if (!validDatasetId) {
      console.warn('datasetId is null');
      return;
    }

    setExporting(true);
    try {
      const response = await exportFAQTree({
        datasetId: validDatasetId,
        language: I18N_LANG_MAP[i18n.language],
      });

      // 创建下载链接
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faq_tree_export_${validDatasetId}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        message: t('faqs.action.export_success'),
        color: 'green.5',
        icon: <IconCheck />,
      });
    } catch (error: any) {
      showNotification({
        message: t('faqs.action.export_error', { error: error.message || 'Unknown error' }),
        color: 'red.5',
        icon: <IconX />,
      });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!selectedKeys.length) {
      setActiveFaq(undefined);
      return;
    }
    const loop = (data: MyTreeNode[]) => {
      data.map((item) => {
        if (selectedKeys.includes(item.key)) {
          detail(item);
          return;
        } else if (item.children?.length) {
          loop(item.children);
        }
      });
    };
    loop(treeData);
  }, [selectedKeys]);

  useEffect(() => {
    // 语言变化时清理旧通知，重新初始化会显示新语言版本
    hideNotification('importing-faq-list');
    init();
  }, [i18n.language]);

  // 组件卸载时清理导入通知
  useEffect(() => {
    return () => {
      hideNotification('importing-faq-list');
    };
  }, []);

  const SortableTreeItem = ({ node }: { node: MyTreeNode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.key });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <TreeItem node={node} dragListeners={listeners} />
      </div>
    );
  };

  const TreeItem = ({ node, dragListeners }: { node: MyTreeNode; dragListeners?: any }) => {
    return (
      <Group
        justify="space-between"
        gap="xs"
        px={5}
        py={8}
        wrap="nowrap"
        onClick={(e) => {
          if (selectedKeys.includes(node.key)) {
            e.stopPropagation();
            return;
          }
          detail(node);
        }}
        {...dragListeners}
        className={`${dragListeners ? 'cursor-grab' : 'cursor-default'}`}
      >
        <section className="flex-1 overflow-hidden">
          <Text className="truncate" size="sm">
            {typeof node.title === 'string' && node.title}
          </Text>
        </section>
        {node.key !== '0' ? (
          <section className="flex-none flex">
            {isDeletingNode(node.key) ? (
              <Loader color="gray.5" size={14} mr={3} />
            ) : (
              <Menu shadow="md">
                <Menu.Target>
                  <ActionIcon
                    className="hide"
                    size={'xs'}
                    variant="subtle"
                    color="gray.5"
                    radius={3}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconDotsVertical />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCloning) {
                        setCloningTipOpened(true);
                        return;
                      }
                      add(String(node.key));
                    }}
                  >
                    {t('faqs.tree.add_child_node')}
                  </Menu.Item>
                  <Menu.Item
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCloning) {
                        setCloningTipOpened(true);
                        return;
                      }
                      del(String(node.key));
                    }}
                  >
                    {t('faqs.tree.del_node')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </section>
        ) : (
          <section className="flex-none flex">
            <ActionIcon
              className="hide"
              size={'xs'}
              variant="subtle"
              color="red.5"
              radius={3}
              onClick={(e) => {
                e.stopPropagation();
                del(String(node.key));
              }}
            >
              <IconX />
            </ActionIcon>
          </section>
        )}
      </Group>
    );
  };

  return (
    <>
      <section className="bg-white rounded-lg flex px-[20px] relative">
        <section className={cx('flex-none w-[350px] overflow-hidden', classes.treeWrap)}>
          <Group gap="xs" mb="xs">
            <Button
              size="xs"
              variant="subtle"
              leftSection={isAllExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              onClick={handleToggleExpand}
              disabled={treeData.length === 0}
            >
              {isAllExpanded ? t('faqs.tree.collapse_all') : t('faqs.tree.expand_all')}
            </Button>
          </Group>
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={getAllNodeKeys(treeData)} strategy={verticalListSortingStrategy}>
              <Tree
                treeData={treeData}
                titleRender={(node: MyTreeNode) => <SortableTreeItem node={node} />}
                selectedKeys={selectedKeys}
                onSelect={onSelect}
                expandedKeys={expandedKeys}
                onExpand={onExpand}
                showLine
              />
            </SortableContext>
            <DragOverlay>{activeId ? <TreeItem node={findNodeById(treeData, activeId)!} /> : null}</DragOverlay>
          </DndContext>
          <Button mt={10} size="sm" loading={treeLoading} radius={8} fullWidth onClick={() => add()}>
            <Group gap={5}>
              <IconCirclePlus size={16} />
              <Text size="sm" fw={300}>
                {t('faqs.tree.add_new_start_point')}
              </Text>
            </Group>
          </Button>
        </section>
        <section className="flex-1 p-5">
          <Group justify="flex-end">
            <Button
              variant="light"
              leftSection={<IconDownload size={16} className="opacity-80" />}
              onClick={handleExport}
              loading={exporting}
            >
              {t('faqs.action.export')}
            </Button>
            <Button leftSection={<IconUpload size={16} className="opacity-80" />} onClick={openImportModal}>
              {t('faqs.action.import')}
            </Button>
          </Group>
          {faqLoading ? (
            <Center mih={500}>
              <Loader size={'sm'} />
            </Center>
          ) : !activeFaq || !selectedKeys.length ? (
            <Center mih={500}>
              <Text size="sm" c="gray">
                {treeData.length ? t('faqs.tree.select_tree_node') : t('faqs.tree.create_tree_node')}
              </Text>
            </Center>
          ) : (
            <FaqsItemEdit
              datasetId={datasetId}
              faqs={activeFaq}
              faqId={activeFaq.id !== '0' ? activeFaq.id : null}
              citation={[]}
              onSubmit={onDetailNodeSubmit}
              onSubmitText={t('faqs.action.add')}
              footerComponent={({ disabled, loading }) => (
                <Stack mt={8}>
                  <Button type="submit" loading={loading} disabled={disabled} size={'sm'} w={100}>
                    {t('save')}
                  </Button>
                </Stack>
              )}
            />
          )}
        </section>
      </section>

      <Modal
        centered
        opened={addConfirmOpened}
        onClose={addConfirmClose}
        title={t('faqs.tree.add_child_node')}
        zIndex={202}
      >
        <Text size="sm">{t('faqs.tree.confirm_add_child_node')}</Text>

        <Group justify="flex-end" mt={30}>
          <Button variant="outline" onClick={addConfirmClose}>
            {t('alert_cancel')}
          </Button>
          <Button onClick={() => add(addNodeParentKeyRef.current, true)}>{t('alert_ok')}</Button>
        </Group>
      </Modal>

      <Modal
        centered
        opened={deleteConfirmOpened}
        onClose={deleteConfirmClose}
        title={t('faqs.tree.del_node')}
        zIndex={202}
      >
        <Text size="sm">
          {t('faqs.tree.confirm_delete_node_with_children', { nodeName: deleteNodeTitleRef.current })}
        </Text>

        <Group justify="flex-end" mt={30}>
          <Button variant="outline" onClick={deleteConfirmClose}>
            {t('alert_cancel')}
          </Button>
          <Button
            color="red"
            onClick={() => {
              executeDelete(deleteNodeKeyRef.current);
              deleteConfirmClose();
            }}
          >
            {t('alert_ok')}
          </Button>
        </Group>
      </Modal>

      <ImportModal opened={importModalOpened} onClose={onCloseImportModal} datasetId={datasetId} />
    </>
  );
};
